package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.Plan
import dev.lingvo.shared.ScreenshotQuota
import dev.lingvo.site.components.marketing.MarketingPageColumnStyle
import dev.lingvo.site.components.marketing.MarketingSectionStyle
import dev.lingvo.site.components.marketing.MultiplicationBlock
import dev.lingvo.site.components.marketing.PublicActionLink
import dev.lingvo.site.components.marketing.PublicFooter
import dev.lingvo.site.components.marketing.PublicHeader
import dev.lingvo.site.components.marketing.SectionHeadingPublic
import dev.lingvo.site.components.marketing.formatThousands
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

/**
 * Pricing.
 *
 * Built from the same [Plan] enum the server enforces, so the page cannot quietly disagree with what
 * a workspace is actually charged or allowed.
 */
@Page("/pricing")
@Composable
fun PricingPage() {
    LaunchedEffect(Unit) {
        document.title = tr("pricing.pricing_lingvo", "Pricing — Lingvo")
    }
    Div(Modifier.fillMaxWidth().toAttrs()) {
        PublicHeader()
        Main {
            Div(MarketingSectionStyle.toModifier().toAttrs()) {
                Column(MarketingPageColumnStyle.toModifier()) {
                    PricingIntro()
                    PlanGrid()
                    LMuted(
                        t(
                            "pricing.monthly_billing_terms",
                            "Prices shown are in US dollars, excluding applicable tax. Paid subscriptions " +
                                "are billed monthly in advance and renew until cancelled. Checkout shows " +
                                "the currency and total before you pay. An owner can cancel in Settings → " +
                                "Billing; the paid plan continues until the end of the paid period.",
                        ),
                    )
                    LMuted(
                        t(
                            "pricing.how_to_upgrade",
                            "Start with a Free workspace. When you need Studio, an owner can open " +
                                "Settings → Billing → Plans to subscribe where checkout is available. " +
                                "Free does not upgrade or charge you automatically. Contact us for Team.",
                        ),
                    )
                    // Moved off the landing page. There it was the second LTable in sixty lines of
                    // scroll, styled identically to the first, so a reader could not tell why there
                    // were two tables or which one was the argument. Here it is a clarification of
                    // the plan cards directly above it.
                    P(Modifier.toAttrs { attr("data-screenshot-pricing", "") }) {
                        Text(t("pricing.screenshot_storage", "Renders and pushes are not metered. Screenshot storage is limited by bytes per project and workspace, never by language. Free includes one app, both stores and the retake queue with no screenshot paywall; Studio and Team allow unlimited projects. CI captures are planned, not available."))
                    }
                    WhoPaysBlock()
                    CreditsExplainer()
                    MultiplicationBlock()
                    Promises()
                    // What an outage actually costs a customer, on the page where they decide
                    // whether to depend on us. It is a paragraph rather than a status dashboard
                    // because the answer does not vary: the files are already in their repository.
                    IfWeAreDown()
                }
            }
        }
        PublicFooter()
    }
}

@Composable
private fun PricingIntro() {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.maxWidth(46.cssRem).gap(Space.MD)) {
        Span(
            Modifier
                .color(palette.textSecondary)
                .fontSize(TypeScale.Micro)
                .fontWeight(600)
                .styleModifier {
                    property("text-transform", "uppercase")
                    property("letter-spacing", "0.08em")
                }
                .toAttrs(),
        ) { Text(t("pricing.pricing", "Pricing")) }
        H1(
            PricingIntroHeadingStyle.toModifier().toAttrs(),
        ) { Text(t("pricing.pay_for_the_people_who_own", "Pay for the people who own the work.")) }
        P(
            Modifier
                .color(palette.textSecondary)
                .fontSize(TypeScale.Lead)
                .lineHeight(TypeScale.BodyLineHeight)
                .margin(0.px)
                .toAttrs(),
        ) {
            Text(
                t(
                    "pricing.shared_workflow_on_every_plan",
                    "Free starts at $0. Studio bills owner and admin seats. Stored keys, words " +
                        "and languages are unmetered; machine translation uses credits, or " +
                        "your own provider key.",
                ),
            )
        }
    }
}

val PricingIntroHeadingStyle = CssStyle {
    base {
        Modifier
            .displayFont()
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .margin(0.px)
    }
    Breakpoint.ZERO { Modifier.fontSize(TypeScale.Heading) }
    Breakpoint.MD { Modifier.fontSize(TypeScale.Display) }
}

@Composable
private fun PlanGrid() {
    Div(PlanGridStyle.toModifier().toAttrs()) {
        Plan.entries.forEach { plan -> PlanCard(plan) }
    }
}

@Composable
private fun PlanCard(plan: Plan) {
    val palette = ColorMode.current.toSitePalette()
    LCard(
        Modifier
            .fillMaxWidth()
            .attr("data-pricing-plan", plan.name)
            .border(1.px, LineStyle.Solid, palette.border)
            .borderRadius(Radius.Card)
            .padding(Space.XL)
            .styleModifier {
                property("display", "flex")
                property("min-width", "0")
                property("flex-direction", "column")
                property("gap", Space.LG.toString())
            },
    ) {
        Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            H2(
                Modifier
                    .displayFont()
                    .fontSize(TypeScale.HeadingSmall)
                    .fontWeight(700)
                    .margin(0.px)
                    .toAttrs(),
            ) { Text(plan.displayName) }
            if (plan == Plan.STUDIO) LBadge(t("pricing.monthly", "Monthly"), variant = LBadgeVariant.ACCENT)
            if (plan == Plan.TEAM) LBadge(t("pricing.on_request", "On request"), variant = LBadgeVariant.NEUTRAL)
        }

        Column(Modifier.gap(Space.XXS)) {
            Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.Bottom) {
                Span(
                    Modifier
                        .displayFont()
                        .fontSize(TypeScale.Display)
                        .fontWeight(700)
                        .lineHeight(1)
                        .tabularNumbers()
                        .styleModifier { property("letter-spacing", "-0.02em") }
                        .toAttrs(),
                ) { Text(if (plan.pricePerSeat == 0) t("pricing.free", "Free") else "$${plan.pricePerSeat}") }
                if (plan.pricePerSeat > 0) {
                    Span(
                        Modifier
                            .color(palette.textSecondary)
                            .fontSize(TypeScale.Body)
                            .padding(bottom = Space.XS)
                            .toAttrs(),
                    ) { Text(t("pricing.seat_mo", "/ seat / mo")) }
                }
            }
            Span(
                Modifier.color(palette.textMuted).fontSize(TypeScale.Small).toAttrs(),
            ) {
                Text(
                    if (plan.pricePerSeat == 0) {
                        t("pricing.no_card_no_expiry", "No card, no expiry")
                    } else {
                        t("pricing.owners_and_admins_only_translators_are", "Owners and admins only — translators are free")
                    },
                )
            }
        }

        Ul(
            Modifier
                .margin(0.px)
                .padding(left = Space.LG)
                .color(palette.textSecondary)
                .fontSize(TypeScale.BodySmall)
                .lineHeight(TypeScale.BodyLineHeight)
                .styleModifier {
                    property("display", "grid")
                    property("gap", Space.XS.toString())
                }
                .toAttrs(),
        ) {
            Li { Text(t("pricing.unlimited_keys_languages_and_words", "Unlimited keys, languages and words")) }
            Li { Text(t("pricing.unlimited_translator_and_viewer_seats_always", "Unlimited translator and viewer seats, always free")) }
            // `tPlural`, not `+ if (limit == 1) "" else "s"`. That is English's rule wearing a
            // general name, on the page whose own copy sells real plurals — and it is wrong in most
            // languages: German does not pluralise "Projekt" with an s, Polish needs a third form at
            // five, Japanese needs one. The `"s"` branch happens to be dead today, because Free is
            // the only plan with limits and both of its limits are 1, so the defect is latent in
            // English and live in every other language the moment a plan grows a second project.
            Li {
                Text(
                    plan.maxProjects?.let { limit ->
                        tPlural("pricing.count.project", limit, "{count} project", "{count} projects")
                    } ?: t("pricing.unlimited_projects", "Unlimited projects"),
                )
            }
            Li {
                Text(
                    plan.maxBillableSeats?.let { limit ->
                        tPlural(
                            "pricing.count.billable_seat",
                            limit,
                            "{count} owner or admin seat",
                            "{count} owner or admin seats",
                        )
                    } ?: t("pricing.unlimited_owner_and_admin_seats", "Unlimited owner and admin seats"),
                )
            }
            Li {
                Text(
                    tf(
                        "pricing.credits_ai_credits_per_seat_each",
                        "{credits} AI credits per seat each month",
                        "credits" to formatThousands(plan.creditsPerSeat),
                    ),
                )
            }
            Li { Text(t("pricing.bring_your_own_provider_key_uses", "Bring your own provider key — uses no credits")) }
            Li { Text(t("pricing.cli_ci_gate_and_api_tokens", "CLI, CI gate and API tokens")) }
            Li { Text(t("pricing.screenshots_included", "Screenshots and push on both stores, including Free")) }
            // "Daily backups" was sold here and implemented nowhere — no schedule, no restore
            // path, nothing gated on the plan. Full history is real: every translation edit is
            // recorded and readable in the editor, on every plan including Free.
            Li { Text(t("pricing.full_edit_history_on_every_string", "Full edit history on every string")) }
        }

        ScreenshotPlanCapacity(plan)

        // `if (plan == TEAM) "/signup" else "/signup"` — a dead conditional, both branches the same.
        // So the card badged "On request" with a button reading "Get in touch" dropped the
        // highest-intent visitor on an account form with no way to make the request. Team now goes
        // where its own label promises, to the address the refunds and help pages already use.
        PublicActionLink(
            path = when (plan) {
                Plan.TEAM -> "mailto:support@lingvo.dev?subject=Lingvo%20Team%20plan"
                else -> "/signup"
            },
            label = when (plan) {
                Plan.FREE -> t("pricing.start_free", "Start free")
                Plan.STUDIO -> t("pricing.start_free_upgrade_later", "Start free, upgrade later")
                Plan.TEAM -> t("pricing.get_in_touch", "Get in touch")
            },
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

/** Defaults explain the offer; actual workspace limits can include operator overrides. */
@Composable
internal fun ScreenshotPlanCapacity(plan: Plan) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.SM).attr("data-screenshot-defaults", plan.name)) {
        Span(Modifier.fontWeight(600).fontSize(TypeScale.BodySmall).toAttrs()) {
            Text(t("pricing.default_screenshot_storage", "Default screenshot storage"))
        }
        TagElement<HTMLElement>("dl", applyAttrs = Modifier.fillMaxWidth().margin(0.px).styleModifier {
            property("display", "grid")
            property("gap", Space.SM.toString())
        }.toAttrs()) {
            val capacities = listOf(
                Triple("project", t("pricing.screenshot_per_project", "Per project"), ScreenshotQuota.projectBytes(plan)),
                Triple("workspace", t("pricing.screenshot_per_workspace", "Per workspace"), ScreenshotQuota.workspaceBytes(plan)),
            )
            for ((scope, label, bytes) in capacities) {
                Div(Modifier.attr("data-screenshot-capacity", scope).toAttrs()) {
                    TagElement<HTMLElement>("dt", applyAttrs = Modifier.color(palette.textSecondary).fontSize(TypeScale.Small).toAttrs()) { Text(label) }
                    TagElement<HTMLElement>("dd", applyAttrs = Modifier.margin(0.px).fontSize(TypeScale.BodySmall).tabularNumbers().styleModifier {
                        property("overflow-wrap", "anywhere")
                    }.toAttrs()) {
                        val gib = 1024L * 1024L * 1024L
                        val capacity = if (bytes >= gib && bytes % gib == 0L) {
                            tf("pricing.screenshot_capacity_gib", "{count} GiB", "count" to formatCount(bytes / gib))
                        } else {
                            tf("pricing.screenshot_capacity_mib", "{count} MiB", "count" to formatCount(bytes / (1024L * 1024L)))
                        }
                        Text(tf("pricing.screenshot_capacity_bytes", "{capacity} ({bytes} bytes)", "capacity" to capacity, "bytes" to formatCount(bytes)))
                    }
                }
            }
        }
        LMuted(t("pricing.screenshot_default_overrides", "Workspace overrides may change these limits. Your screenshot usage meter shows actual limits."))
    }
}

/**
 * The four roles and what each costs.
 *
 * This is the translator's section as much as the buyer's, and the fastest way to say "you are not a
 * line item here" is a table with two zeroes in it.
 *
 * **It is not a competitive claim, and must not become one.** Crowdin, Lokalise, Weblate and POEditor
 * all give translators free seats too; Transifex and Tolgee count them against a cap. The table
 * states what *we* charge, which is true without needing anyone else to be worse.
 */
@Composable
private fun WhoPaysBlock() {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.XXXL)) {
        SectionHeadingPublic(
            id = "who-pays-title",
            title = t("pricing.two_of_these_four_cost_nothing", "Two of these four cost nothing"),
            description = t(
                "pricing.translators_are_invited_and_unlimited_on",
                "Translators are invited and unlimited on every plan, including free.",
            ),
        )
        // "(USD)" in the caption rather than beside each figure: the column is four numbers in the
        // same currency, and the caption is where a table says what its numbers are.
        LTable(caption = t("pricing.cost_per_person_per_month", "Cost per person, per month (USD)")) {
            LTableHead {
                LTableRow {
                    LTableHeaderCell { Text(t("pricing.role", "Role")) }
                    LTableHeaderCell { Text(t("pricing.can_do", "Can do")) }
                    LTableHeaderCell { Text(t("pricing.cost", "Cost")) }
                }
            }
            LTableBody {
                // The third element is the price, not a rendered string. It used to be `"$0"`, and
                // the accent that marks a free row was decided by comparing against that literal —
                // so the eight role and permission strings could not be translated without silently
                // un-marking two rows. The number decides now, and the `$` is formatting.
                listOf(
                    Triple(
                        t("pricing.owner", "Owner"),
                        t("pricing.everything_including_billing", "Everything, including billing"),
                        Plan.STUDIO.pricePerSeat,
                    ),
                    Triple(
                        t("pricing.admin", "Admin"),
                        t("pricing.structure_members_providers", "Structure, members, providers"),
                        Plan.STUDIO.pricePerSeat,
                    ),
                    Triple(
                        t("pricing.translator", "Translator"),
                        t("pricing.write_and_approve_translations", "Write and approve translations"),
                        0,
                    ),
                    Triple(
                        t("pricing.viewer", "Viewer"),
                        t("pricing.read_everything_change_nothing", "Read everything, change nothing"),
                        0,
                    ),
                ).forEach { (role, can, seatPrice) ->
                    val free = seatPrice == 0
                    LTableRow {
                        LTableCell { Text(role) }
                        LTableCell(Modifier.color(palette.textSecondary)) { Text(can) }
                        LTableCell(
                            Modifier
                                .tabularNumbers()
                                .color(if (free) palette.accent else palette.textPrimary)
                                .fontWeight(if (free) 600 else 400),
                        ) { Text("$" + seatPrice) }
                    }
                }
            }
        }
    }
}

@Composable
private fun CreditsExplainer() {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.maxWidth(46.cssRem).gap(Space.MD)) {
        H2(
            Modifier.displayFont().fontSize(TypeScale.HeadingSmall).fontWeight(700).margin(0.px).toAttrs(),
        ) { Text(t("pricing.what_a_credit_is", "What a credit is")) }
        P(
            Modifier
                .color(palette.textSecondary)
                .fontSize(TypeScale.BodySmall)
                .lineHeight(TypeScale.BodyLineHeight)
                .margin(0.px)
                .toAttrs(),
        ) {
            Text(
                t(
                    "pricing.one_credit_is_one_ui_string",
                    "One credit is one UI string of up to 1,000 characters, machine-translated into one " +
                        "language. Claude and OpenAI cost three credits, because they cost us more and " +
                        "are worth more. Past " +
                        "1,000 characters a string costs one more credit per 1,000, which almost no " +
                        "interface text ever reaches. Credits reset monthly, and running out never stops " +
                        "you translating by hand, importing, exporting or running QA — it stops the " +
                        "machine, not the team.",
                ),
            )
        }
    }
}

/**
 * The degradation story, on the page where somebody decides whether to depend on us.
 *
 * Every buyer asks what happens if the vendor goes down, and the honest answer here is unusually
 * good — so not saying it is leaving the strongest argument on the table. It is also the answer that
 * makes a status page unnecessary: nothing at build time calls us, so an outage costs the
 * translations added since the last pull and nothing else.
 */
@Composable
private fun IfWeAreDown() {
    val palette = ColorMode.current.toSitePalette()
    // `LCard`, not a `Div` hand-drawn to look like one. The four modifiers this used to carry —
    // sunken ground, 1px border, card radius, padding — are `LCardStyle`, which this same file
    // already uses for the plan cards above; only the ground and the padding differ, and those are
    // what a modifier is for.
    LCard(Modifier.fillMaxWidth().backgroundColor(palette.surfaceSunken).padding(Space.XL)) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            H2(
                Modifier.displayFont().fontSize(TypeScale.HeadingSmall).fontWeight(700).margin(0.px).toAttrs(),
            ) { Text(t("pricing.if_we_are_down_you_still", "If we are down, you still ship")) }
            P(
                Modifier
                    .margin(0.px)
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .toAttrs(),
            ) {
                Text(
                    t(
                        "pricing.your_strings_are_files_in_your",
                        "Your strings are files in your repository. The only thing Lingvo puts in your " +
                            "pipeline is lingvo pull, which writes those files — nothing at build time " +
                            "asks us for a string. So an outage costs you the translations added since " +
                            "your last pull, and your release goes out with the files you already have.",
                    ),
                )
            }
            P(
                Modifier
                    .margin(0.px)
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .toAttrs(),
            ) {
                Text(
                    t(
                        "pricing.that_is_also_why_there_is",
                        "That is also why there is no status page here. It would be the least useful " +
                            "thing we could show you, because the answer never changes.",
                    ),
                )
            }
        }
    }
}

@Composable
private fun Promises() {
    val palette = ColorMode.current.toSitePalette()
    // The same card as `IfWeAreDown`, and for the same reason: this was the second hand-drawn copy
    // of `LCardStyle` on a page that already uses the real one.
    LCard(Modifier.fillMaxWidth().backgroundColor(palette.surfaceSunken).padding(Space.XL)) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            H2(
                Modifier.displayFont().fontSize(TypeScale.HeadingSmall).fontWeight(700).margin(0.px).toAttrs(),
            ) { Text(t("pricing.four_things_we_will_not_do", "Four things we will not do")) }
            Ul(
                Modifier
                    .margin(0.px)
                    .padding(left = Space.LG)
                    .color(palette.textSecondary)
                    .fontSize(TypeScale.BodySmall)
                    .lineHeight(TypeScale.BodyLineHeight)
                    .styleModifier {
                        property("display", "grid")
                        property("gap", Space.XS.toString())
                    }
                    .toAttrs(),
            ) {
                Li { Text(t("pricing.charge_per_word_per_language_or", "Charge for stored words, languages, or keys.")) }
                Li { Text(t("pricing.charge_for_the_people_doing_the", "Charge for the people doing the translating.")) }
                Li { Text(t("pricing.bill_a_free_workspace_by_surprise", "Bill a free workspace by surprise — free is a hard cap, not an overage.")) }
                Li { Text(t("pricing.meter_the_cli_or_the_api", "Meter the CLI or the API. They are how the product is used.")) }
            }
        }
    }
}

val PlanGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.LG)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "minmax(0, 1fr)")
                property("align-items", "start")
            }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("grid-template-columns", "repeat(3, minmax(0, 1fr))") }
    }
}
